/*
 * Koro's own storefront behaviour. Loaded on every page from
 * layout/theme.liquid, next to custom.css.
 *
 * It exists because both things below need to run site-wide, and the only
 * other way to load a script everywhere is a section, which renders a box.
 * An empty custom-liquid section is not empty: its `.rte` child carries a
 * 16px bottom margin that collapses out through the section and through
 * #MainContent, so the whole page sat 16px lower and a white strip showed
 * above the hero. That is what this file replaced.
 */
(function () {
  // Read now: a deferred script's currentScript is gone by the time an event
  // handler runs. It carries the translated labels (see layout/theme.liquid).
  var SCRIPT = document.currentScript;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    sharedHeroCaptions();
    overlayHeader();
    markCurrentMenuItem();
    centreHeaderMenuGap();
    reviewCarousels();
    heroSearchFields();
    sectionAnchors();
    tileRowArrows();
    draggableSlideshows();
  }

  /* ----------------------------------------------------------------------
   * Mark the menu item for the page you are on.
   *
   * Envy marks the current item in the mobile drawer and **not** in the
   * desktop menu: `sections/header.liquid` emits `mobile-nav__item--active`
   * from `link.active`, and the desktop list gets nothing. So on desktop every
   * item looked the same wherever you were.
   *
   * Done here rather than by editing the stock section. It also catches cases
   * Liquid's `link.active` misses: an article counts as the blog, and a
   * collection counts as Shop, because the dropdown's own children are
   * matched too.
   *
   * Longest match wins, so a child link beats its parent and the most specific
   * item is the one marked.
   * -------------------------------------------------------------------- */
  function markCurrentMenuItem() {
    var nav = document.querySelector('.site-nav');
    if (!nav) return;

    var here = normalisePath(location.pathname);
    var best = null;
    var bestLength = -1;

    nav.querySelectorAll(':scope > li').forEach(function (li) {
      li.querySelectorAll('a[href]').forEach(function (link) {
        var path;
        try {
          path = normalisePath(new URL(link.href, location.origin).pathname);
        } catch (e) {
          return;
        }
        // The storefront root would match everything.
        if (path === '/') return;

        var isHere = here === path || here.indexOf(path + '/') === 0;
        if (isHere && path.length > bestLength) {
          bestLength = path.length;
          best = li;
        }
      });
    });

    if (best) best.classList.add('koro-nav--current');
  }

  // The store is Indonesian at the root and English under /en/, and menu links
  // are stored with the locale in them. Compare without it, and without a
  // trailing slash, or the marking works in one language and not the other.
  function normalisePath(path) {
    var out = path.replace(/\/+$/, '') || '/';
    out = out.replace(/^\/(en|id)(?=\/|$)/, '');
    return out || '/';
  }

  /* ----------------------------------------------------------------------
   * The logo sits in the middle of the menu, not the middle of the page.
   *
   * The split header is one centred list with a wide left margin on the first
   * item of the second half, and the logo is centred on the page in its own
   * grid slot. Those two centres only coincide when the two halves of the menu
   * are the same width, and Koro's are not: "Shop Events Blog" is 201px and
   * "About Us Gallery Contact" is 277px, so the gap lands 38px left of the
   * logo and the mark ends up jammed against "About Us".
   *
   * No amount of CSS fixes this. Every content-agnostic way of distributing
   * free space -- auto margins, flex-grow, fr columns -- splits it evenly, and
   * an even split always leaves the gap's centre at (left - right) / 2 from
   * the page centre. Centring it needs an uneven split, which needs to know
   * how wide the two halves actually are. So it is measured.
   *
   * The menu moves, not the logo: the design puts the mark on the page centre,
   * and the icons stay at the right edge. A transform is used so nothing
   * reflows. It re-measures on resize and once the webfonts have loaded, since
   * a fallback font changes every width here.
   * -------------------------------------------------------------------- */
  function centreHeaderMenuGap() {
    var wrapper = document.querySelector('.site-header__wrapper--logo-center');
    if (!wrapper) return;

    var nav = wrapper.querySelector('.site-nav');
    if (!nav) return;

    var apply = function () {
      // Measure with the shift removed, or each pass reads its own last answer.
      nav.style.setProperty('--koro-header-menu-shift', '0px');

      var items = nav.querySelectorAll(':scope > li');
      var n = items.length;
      // The gap only exists for an even menu, and only 4, 6 and 8 are styled.
      if (n < 4 || n > 8 || n % 2 !== 0) return;

      var half = n / 2;
      var leftEnd = items[half - 1].getBoundingClientRect().right;
      var rightStart = items[half].getBoundingClientRect().left;
      var gap = rightStart - leftEnd;

      // Below the one-row breakpoint the menu has its own row and no gap. The
      // ordinary space between two links is nowhere near this wide.
      if (gap < 60) return;

      var logo = wrapper.querySelector('.site-header__wrapper__center');
      var box = (logo || wrapper).getBoundingClientRect();
      var shift = box.left + box.width / 2 - (leftEnd + gap / 2);

      nav.style.setProperty('--koro-header-menu-shift', Math.round(shift) + 'px');
    };

    apply();
    window.addEventListener('resize', apply, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(apply);
  }

  /* ----------------------------------------------------------------------
   * One caption over every slide.
   *
   * Envy draws a caption inside each slide and hides it on every slide but
   * the active one (`display: none !important` in section-slideshow.min.css),
   * so when the slides carry the same words the text still vanishes and
   * reappears with each photograph. The design wants the words to stay put
   * while the photographs change.
   *
   * So when every slide's caption says the same thing and points at the same
   * places, one copy is lifted out of the slides into a layer above them, and
   * the per-slide copies are hidden. It is decided by content, not by page:
   * give the slides different captions and each slide keeps its own again.
   * The layer keeps the first slide's position and colour classes, so Envy's
   * own caption styles place and colour it. It runs before the hero search
   * wiring, so the lifted search field is the one that gets wired.
   * -------------------------------------------------------------------- */
  function sharedHeroCaptions() {
    document.querySelectorAll('.section--slideshow-wrapper .swiper').forEach(function (swiperEl) {
      var slides = Array.prototype.filter.call(
        swiperEl.querySelectorAll('.swiper-slide'),
        function (slide) { return !slide.classList.contains('swiper-slide-duplicate'); }
      );
      if (slides.length < 2) return;

      var signature = function (slide) {
        var box = slide.querySelector('.homepage-featured-content-box');
        if (!box) return null;
        var links = Array.prototype.map.call(box.querySelectorAll('a[href]'), function (a) {
          return a.getAttribute('href');
        });
        return box.textContent.replace(/\s+/g, ' ').trim() + '|' + links.join(',');
      };

      var first = signature(slides[0]);
      if (!first) return;
      for (var i = 1; i < slides.length; i++) {
        if (signature(slides[i]) !== first) return;
      }

      var source = slides[0];
      var layer = document.createElement('div');
      var keep = Array.prototype.filter.call(source.classList, function (c) {
        return /^(homepage-sections--slideshow-wrapper|block-section--|text-position--|mobile-text-position--)/.test(c);
      });
      layer.className = 'koro-shared-caption ' + keep.join(' ');

      var caption = source.querySelector('.homepage-featured-content-box').cloneNode(true);
      caption.querySelectorAll('[id]').forEach(function (el) { el.removeAttribute('id'); });
      // Envy's slideshow takes every `.homepage-section-btn` inside the slider
      // out of the tab order on each slide change, except the active slide's,
      // and this layer sits inside the slider. It is always showing, so its
      // buttons drop that class (the only thing Envy keys on) and stay
      // reachable by keyboard.
      caption.querySelectorAll('.homepage-section-btn').forEach(function (el) {
        el.classList.remove('homepage-section-btn');
        el.classList.add('koro-shared-caption__btn');
      });
      caption.querySelectorAll('[tabindex="-1"]').forEach(function (el) { el.removeAttribute('tabindex'); });
      layer.appendChild(caption);

      var wrapper = swiperEl.querySelector('.swiper-wrapper');
      wrapper.parentNode.insertBefore(layer, wrapper.nextSibling);
      swiperEl.classList.add('koro-has-shared-caption');
    });
  }

  /* ----------------------------------------------------------------------
   * Transparent header over a full-bleed hero.
   *
   * Envy offers its overlay header on the homepage, collections, the
   * collections list, articles and the contact page only: the condition in
   * sections/header.liquid tests `template`, so a page template can never opt
   * in. Shop and Events are drawn with the header floating on the hero, so the
   * two things Envy would have done are done here instead.
   *
   * It cannot be CSS alone, for two reasons found by measuring rather than
   * assuming. On a non-overlay template Envy never adds its own `sticked`
   * class, so there is no signal to switch back to the solid bar on scroll;
   * and it does not render the white overlay logo at all, so there is nothing
   * to reveal. This adds the signal, and custom.css inverts the black mark.
   *
   * The condition is structural rather than a list of page handles: a page
   * that opens with a full-bleed slideshow or image overlay (Shop, Events,
   * Contact, the blog), on a template where Envy did not already apply its
   * own overlay. Add such a page and it just works.
   *
   * Since 2026-09-24 every page behaves like the homepage at the top, at the
   * operator's request: a page without a hero gets the light variant, which
   * drops the bar and its rule until the page scrolls but keeps dark ink,
   * because there is no photograph under it to carry cream text.
   * -------------------------------------------------------------------- */
  function overlayHeader() {
    var section = document.querySelector('.header-section');
    if (!section) return;

    var main = document.getElementById('MainContent');
    var first = main && main.querySelector('.shopify-section');
    var hero =
      first &&
      (first.classList.contains('section--slideshow-wrapper') ||
        first.classList.contains('section--image-with-text-overlay'));
    var measure = function () {
      document.documentElement.style.setProperty(
        '--koro-header-height',
        Math.round(section.getBoundingClientRect().height) + 'px'
      );
    };

    // An image-overlay hero centres its text in its own box, and with the
    // header floating over it that box starts under the header, so the title
    // came up against the menu. The hero is told the header's height. This
    // holds whoever draws the overlay, Envy (Contact, by its setting) or us.
    if (hero) first.classList.add('koro-under-header');
    measure();
    window.addEventListener('resize', measure, { passive: true });

    // Envy is already doing this here. Doing it twice fights its own styles.
    if (section.querySelector('.overlay-header-wrapper')) return;

    // Every other page opens on the page's own background. The header there is
    // "transparent" in the only way that shows: no bar and no rule at the top,
    // dark ink, and the solid bar once the page scrolls, as on the homepage.
    section.classList.add(hero ? 'koro-overlay' : 'koro-overlay-light');

    var sync = function () {
      var h = section.getBoundingClientRect().height || 100;
      section.classList.toggle('koro-overlay--stuck', window.scrollY > h * 0.6);
    };

    sync();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
  }

  /* ----------------------------------------------------------------------
   * The review wall's two arrows.
   *
   * The row itself is a scroll-snap list and needs nothing from here: it
   * already scrolls with a trackpad, a swipe and the arrow keys. This only
   * wires the buttons and greys them out at each end, so the row never has a
   * control that looks live and does nothing.
   * -------------------------------------------------------------------- */
  function reviewCarousels() {
    document.querySelectorAll('[data-koro-reviews]').forEach(function (section) {
      var track = section.querySelector('[data-koro-reviews-track]');
      var prev = section.querySelector('[data-koro-reviews-prev]');
      var next = section.querySelector('[data-koro-reviews-next]');
      if (!track || !prev || !next) return;

      // Step by whole cards, so a card never stops half out of view.
      var step = function () {
        var card = track.querySelector('li');
        if (!card) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var per = Math.max(1, Math.floor(track.clientWidth / (card.offsetWidth + gap)));
        return (card.offsetWidth + gap) * per;
      };

      var sync = function () {
        var max = track.scrollWidth - track.clientWidth;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
      };

      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /* ----------------------------------------------------------------------
   * A hero button that is painted as a search field.
   *
   * The design puts a search box in the hero. Envy's slideshow captions hold
   * buttons, not inputs, and a real input would be a second search form to
   * keep in step with the header's and a second search landmark to announce.
   * So any slideshow button pointing at /search is dressed as the field by
   * custom.css and opens the header's own predictive search here.
   *
   * Placing one is a setting, not code: point a slide's button at /search.
   * -------------------------------------------------------------------- */
  function heroSearchFields() {
    var triggers = document.querySelectorAll(
      '.section--slideshow-wrapper .slide-buttons a[href$="/search"]'
    );
    if (!triggers.length) return;

    var headerSearch = document.querySelector('.search-button');
    if (!headerSearch) return;

    // The href is stored as a bare "/search" in a section setting, because a
    // JSON template cannot hold Liquid and Shopify has no `shopify://` form for
    // search. That drops the locale: on the English storefront the theme's own
    // header search points at /en/search while this pointed at /search. It only
    // shows when the click handler is bypassed — script blocked, or a
    // middle-click into a new tab — but then an English or German shopper lands
    // on the Indonesian search page. `Shopify.routes.root` is the locale root
    // ("/", "/en/", "/de/"), so the link is rewritten to match the storefront
    // the visitor is actually on. Markets matter here: this store also serves
    // kororoasters.com in en, es and de.
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    Array.prototype.forEach.call(triggers, function (trigger) {
      // It stays a real link to the search page, so a blocked script degrades
      // to a working search. The button role is only true once this listener
      // exists.
      trigger.setAttribute('href', root + 'search');
      trigger.setAttribute('role', 'button');

      trigger.addEventListener('click', function (event) {
        event.preventDefault();

        // openDropdown() binds a click-outside listener on document while
        // this very click is still bubbling, so without this the panel opens
        // and the same click closes it again. Envy's own button escapes this
        // because its handler stops propagation on the click it is handed; a
        // synthetic click starts a separate dispatch, so ours survives.
        event.stopPropagation();

        var dropdown = document.querySelector('search-dropdown');
        var alreadyOpen =
          dropdown && dropdown.getAttribute('aria-hidden') === 'false';
        if (!alreadyOpen) headerSearch.click();

        focusWhenVisible(dropdown);
      });
    });
  }

  // The header focuses its own input once the panel has finished opening, but
  // only on pointer devices. Wait for the field to become visible, then focus
  // it only if nothing inside the panel has taken focus, so we never fight it.
  function focusWhenVisible(dropdown) {
    var frames = 0;
    var tick = function () {
      var input =
        (dropdown &&
          dropdown.querySelector('[data-search-dropdown-initial-focus]')) ||
        document.querySelector('input[name="q"]');

      if (input && getComputedStyle(input).visibility !== 'hidden') {
        if (!dropdown || !dropdown.contains(document.activeElement)) {
          input.focus();
        }
        return;
      }
      if (++frames < 60) window.requestAnimationFrame(tick);
    };
    tick();
  }

  /* ----------------------------------------------------------------------
   * Links to a section by its key.
   *
   * A button can point at another section of the same page by the section's
   * key in the template, e.g. `#shop_looking_for` (Shop's "SHOP NOW" does).
   * Shopify renders a section's id as `shopify-section-<template id>__<key>`,
   * where the template id differs per theme, so a literal anchor would break
   * on the next theme; the key does not. The link is a setting, the target is
   * found here, and the scroll respects reduced motion. `custom.css` gives
   * sections a scroll margin the height of the header, so the section is not
   * hidden under it. Without the script the link does nothing, which is the
   * whole of the degradation.
   * -------------------------------------------------------------------- */
  function sectionAnchors() {
    var find = function (hash) {
      var key = decodeURIComponent((hash || '').replace(/^#/, ''));
      if (!key || document.getElementById(key)) return null;
      return document.querySelector('.shopify-section[id$="__' + key.replace(/"/g, '') + '"]');
    };
    var go = function (target, smooth) {
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: smooth && !reduce ? 'smooth' : 'auto', block: 'start' });
    };

    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('a[href^="#"]');
      if (!link) return;
      var target = find(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      go(target, true);
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', link.getAttribute('href'));
      }
    });

    var initial = find(window.location.hash);
    if (initial) go(initial, false);
  }

  /* ----------------------------------------------------------------------
   * Arrows for the sideways tile rows.
   *
   * custom.css lays every collection list out as one row that scrolls
   * sideways. Touch, a trackpad and the scrollbar all move it, but a mouse
   * without horizontal scroll had only the thin bar. Two round buttons sit
   * over the row's edges, as the design draws its sliders: they appear only
   * when the row overflows, the one at an end hides itself, and each press
   * moves by the tiles that are fully in view.
   *
   * The labels are translatable theme strings (`koro.tile_row.*` in the
   * locale files), handed over on this script's own tag, since a script
   * cannot call Liquid's `t` filter.
   * -------------------------------------------------------------------- */
  function tileRowArrows() {
    var labels = (SCRIPT && SCRIPT.dataset) || {};
    var icon = function (d) {
      return '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="' + d + '"/></svg>';
    };

    document.querySelectorAll('.featured-collections-wrapper .collection-grid').forEach(function (row) {
      var host = row.parentElement;
      if (!host || host.querySelector(':scope > .koro-tile-arrow')) return;

      var make = function (dir) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'koro-tile-arrow koro-tile-arrow--' + dir;
        b.setAttribute(
          'aria-label',
          dir === 'prev'
            ? labels.labelScrollPrevious || 'Scroll back'
            : labels.labelScrollNext || 'Scroll forward'
        );
        b.innerHTML = icon(dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7');
        host.appendChild(b);
        return b;
      };
      var prev = make('prev');
      var next = make('next');
      host.classList.add('koro-tile-row-host');

      var step = function () {
        var tile = row.querySelector('.homepage-collection-grid-item');
        if (!tile) return row.clientWidth;
        var gap = parseFloat(getComputedStyle(row).columnGap) || 0;
        var w = tile.getBoundingClientRect().width + gap;
        return Math.max(1, Math.floor((row.clientWidth + gap) / w)) * w;
      };
      var move = function (sign) {
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        row.scrollBy({ left: sign * step(), behavior: reduce ? 'auto' : 'smooth' });
      };
      var sync = function () {
        var max = row.scrollWidth - row.clientWidth;
        var overflow = max > 1;
        prev.hidden = !overflow || row.scrollLeft <= 1;
        next.hidden = !overflow || row.scrollLeft >= max - 1;
      };

      prev.addEventListener('click', function () { move(-1); });
      next.addEventListener('click', function () { move(1); });
      row.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      window.addEventListener('load', sync);
      sync();
    });
  }

  /* ----------------------------------------------------------------------
   * Draggable hero slides.
   *
   * sections/slideshow.liquid ties dragging to the pagination style: it emits
   * data-draggable="true" only when the navigation style is "none". So a
   * slideshow with dots cannot be dragged, and the design has both. Rather
   * than edit a stock section, flip the live Swiper instance.
   *
   * Scoped to the slideshow section type, so product galleries and the other
   * sliders that are deliberately not draggable are untouched.
   * -------------------------------------------------------------------- */
  function draggableSlideshows() {
    var tries = 0;

    var flip = function () {
      var pending = 0;

      document
        .querySelectorAll('.section--slideshow-wrapper .swiper')
        .forEach(function (el) {
          if (el.dataset.koroDraggable === 'done') return;

          // The theme initialises Swiper itself, and not necessarily before
          // this runs, so wait for the instance rather than assume it.
          var swiper = el.swiper;
          if (!swiper || !swiper.params) {
            pending++;
            return;
          }

          swiper.params.allowTouchMove = true;
          swiper.allowTouchMove = true;
          swiper.params.grabCursor = true;
          if (typeof swiper.setGrabCursor === 'function') swiper.setGrabCursor();
          el.dataset.koroDraggable = 'done';
        });

      if (pending && ++tries < 50) window.setTimeout(flip, 100);
    };

    flip();
  }
})();
